export enum UserRole {
  ADMIN = 'admin',
  INSTRUCTOR = 'instructor',
  LEARNER = 'learner',
}

export interface User {
  id: string | null
  email: string | null
  firstName: string | null
  lastName: string | null
  role: UserRole | null
  avatar: string | null
  isActive: boolean | null
  lastLoginAt: string | null
  createdAt: string | null
  updatedAt: string | null

  isPasswordTemporary?: boolean | null
  department?: string | null
  departmentName?: string | null
  departmentId?: string | null
  facultyName?: string | null
  program?: string | null
  programmeTitle?: string | null
  year?: string | null
  level?: number | null
  stage?: string | null
  phone?: string | null
  bio?: string | null
  location?: string | null
  dateOfBirth?: string | null
  studentId?: string | null
  enrollmentDate?: string | null
  programmeId?: string | null
  programmeCohortId?: string | null
  programmeCohortAssignedAt?: string | null
  programmeCohortCompletedAt?: string | null
  applicationId?: string | null
}

export class UserModel implements User {
  id: string | null
  email: string | null
  firstName: string | null
  lastName: string | null
  role: UserRole | null
  avatar: string | null
  isActive: boolean | null
  lastLoginAt: string | null
  createdAt: string | null
  updatedAt: string | null

  isPasswordTemporary?: boolean | null = false
  department?: string | null = null
  departmentName?: string | null = null
  departmentId?: string | null = null
  facultyName?: string | null = null
  program?: string | null = null
  programmeTitle?: string | null = null
  year?: string | null = null
  level?: number | null = null
  stage?: string | null = null
  phone?: string | null = null
  bio?: string | null = null
  location?: string | null = null
  dateOfBirth?: string | null = null
  studentId?: string | null = null
  enrollmentDate?: string | null = null
  programmeId?: string | null = null
  programmeCohortId?: string | null = null
  programmeCohortAssignedAt?: string | null = null
  programmeCohortCompletedAt?: string | null = null
  applicationId?: string | null = null

  constructor(data?: Partial<UserModel>) {
    this.id = data?.id ?? null
    this.email = data?.email ?? null
    this.firstName = data?.firstName ?? null
    this.lastName = data?.lastName ?? null
    this.role = data?.role ?? null
    this.avatar = data?.avatar ?? null
    this.isActive = data?.isActive ?? null
    this.lastLoginAt = data?.lastLoginAt ?? null
    this.createdAt = data?.createdAt ?? null
    this.updatedAt = data?.updatedAt ?? null
    this.isPasswordTemporary = data?.isPasswordTemporary ?? false
    this.department = data?.department ?? null
    this.departmentName = data?.departmentName ?? null
    this.departmentId = data?.departmentId ?? null
    this.facultyName = data?.facultyName ?? null
    this.program = data?.program ?? null
    this.programmeTitle = data?.programmeTitle ?? null
    this.year = data?.year ?? null
    this.level = data?.level ?? null
    this.stage = data?.stage ?? null
    this.phone = data?.phone ?? null
    this.bio = data?.bio ?? null
    this.location = data?.location ?? null
    this.dateOfBirth = data?.dateOfBirth ?? null
    this.studentId = data?.studentId ?? null
    this.enrollmentDate = data?.enrollmentDate ?? null
    this.programmeId = data?.programmeId ?? null
    this.programmeCohortId = data?.programmeCohortId ?? null
    this.programmeCohortAssignedAt = data?.programmeCohortAssignedAt ?? null
    this.programmeCohortCompletedAt = data?.programmeCohortCompletedAt ?? null
    this.applicationId = data?.applicationId ?? null
  }

  static fromJSON(json: any): UserModel {
    if (!json) return new UserModel()

    const firstName = json.firstName ?? json.first_name ?? null
    const lastName = json.lastName ?? json.last_name ?? null
    const isActive = json.isActive ?? json.is_active ?? null
    const lastLoginAt = json.lastLoginAt ?? json.last_login_at ?? null
    const createdAt = json.createdAt ?? json.created_at ?? null
    const updatedAt = json.updatedAt ?? json.updated_at ?? null
    const programme = json.programme ?? json.program ?? null
    const programmeTitle =
      typeof programme === 'string' ? programme : programme?.title ?? null
    const programmeDepartment =
      typeof programme === 'string'
        ? null
        : programme?.department?.name ?? programme?.department_name ?? null
    const programmeFaculty =
      typeof programme === 'string'
        ? null
        : programme?.faculty?.name ?? programme?.faculty_name ?? null
    const department = json.department ?? programmeDepartment ?? null
    const departmentId =
      typeof department === 'string' ? null : department?.id ?? json.department_id ?? null
    const departmentName =
      typeof department === 'string'
        ? department
        : department?.name ?? json.department_name ?? null

    return new UserModel({
      id: json.id ?? null,
      email: json.email ?? null,
      firstName,
      lastName,
      role: json.role ?? null,
      avatar: json.avatar ?? null,
      isActive,
      lastLoginAt,
      createdAt,
      updatedAt,
      isPasswordTemporary:
        json.isPasswordTemporary ?? json.is_password_temporary ?? false,
      department:
        typeof json.department === 'string'
          ? json.department
          : json.department?.name ?? departmentName ?? null,
      departmentName,
      departmentId,
      facultyName: programmeFaculty,
      program: programmeTitle,
      programmeTitle,
      year: json.year ?? null,
      level: json.level ?? json.levelNumber ?? null,
      stage: json.stage ?? null,
      phone: json.phone ?? json.phone_number ?? null,
      bio: json.bio ?? null,
      location: json.location ?? null,
      dateOfBirth: json.dateOfBirth ?? json.date_of_birth ?? null,
      studentId: json.studentId ?? json.student_id ?? null,
      enrollmentDate: json.enrollmentDate ?? json.enrollment_date ?? null,
      programmeId: json.programmeId ?? json.programme_id ?? json.program_id ?? null,
      programmeCohortId: json.programmeCohortId ?? json.programme_cohort_id ?? null,
      programmeCohortAssignedAt:
        json.programmeCohortAssignedAt ?? json.programme_cohort_assigned_at ?? null,
      programmeCohortCompletedAt:
        json.programmeCohortCompletedAt ?? json.programme_cohort_completed_at ?? null,
      applicationId: json.applicationId ?? json.application_id ?? null,
    })
  }

  toJSON(): Record<string, any> {
    return {
      id: this.id,
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      role: this.role,
      avatar: this.avatar,
      isActive: this.isActive,
      lastLoginAt: this.lastLoginAt,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      phone: this.phone,
      bio: this.bio,
      location: this.location,
      dateOfBirth: this.dateOfBirth,
      department: this.departmentName ?? this.department,
      departmentId: this.departmentId,
      programme: this.programmeTitle ?? this.program,
      studentId: this.studentId,
      enrollmentDate: this.enrollmentDate,
      facultyName: this.facultyName,
      level: this.level,
      stage: this.stage,
      programmeId: this.programmeId,
      programmeCohortId: this.programmeCohortId,
      programmeCohortAssignedAt: this.programmeCohortAssignedAt,
      programmeCohortCompletedAt: this.programmeCohortCompletedAt,
      applicationId: this.applicationId,
    }
  }

  get fullName(): string {
    return [this.firstName, this.lastName].filter(Boolean).join(' ').trim()
  }

  get initials(): string {
    const firstInitial = this.firstName?.[0] ?? ''
    const lastInitial = this.lastName?.[0] ?? ''
    return (firstInitial + lastInitial).toUpperCase()
  }
}
